<template>
    <v-container fluid>
        <LoadingBar/>
        <v-form ref="form" lazy-validation>
            <v-row>
                <v-col>
                    <h1>Contract Create</h1>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-text-field
                        v-model="entityCore.name"
                        label="Name*"
                        placeholder="Name"
                        hint="Minimum of 3 characters required"
                        persistent-hint
                        validate-on="blur"
                        :rules="[
                            rules.required, 
                            rules.min(3), 
                            rules.max(64)
                        ]"
                    >
                    </v-text-field>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-text-field
                        v-model="entityCore.number"
                        label="Contract Number*"
                        placeholder="Contract Number"
                        validate-on="blur"
                        @blur="convertEmptyToNull('number')"
                        :rules="[
                            rules.required,
                            rules.min(1),
                            rules.max(64),
                        ]"
                    >
                    </v-text-field>
                </v-col>
                <v-col>
                    <v-text-field
                        v-model="entityCore.value"
                        label="Contract Value"
                        placeholder="Contract Value"
                        validate-on="blur"
                        @input="numbersAndDecimalsOnly($event, 'value');"
                        @blur="convertToNumber('value')"
                        :rules="[
                            rules.numberWithDecimal
                        ]"
                    >
                    </v-text-field>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-select
                        v-model="entityCore.type"
                        :items="contractTypeList"
                        item-title="title"
                        item-value="value"
                        label="Contract Type"
                        placeholder="Contract Type"
                        @blur="convertEmptyToNull('type')"
                    >
                    </v-select>
                </v-col>
                <v-col>
                    <v-select
                        v-model="entityCore.procurement_type"
                        :items="contractProcurementTypeList"
                        item-title="title"
                        item-value="value"
                        label="Contract Procurement Type"
                        placeholder="Contract Procurement Type"
                        @blur="convertEmptyToNull('procurement_type')"
                    >
                    </v-select>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="6">
                    <v-select
                        v-model="entityCore.contractor_uid"
                        :items="contractorsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Contractor*"
                        placeholder="Contractor"
                        @focus="fetchList('contractor','all');"
                        :loading="contractorsList.loading"
                        :disabled="contractorsList.disabled"
                        :hint="contractorsList.hint"
                        persistent-hint
                        :rules="[
                            rules.required,
                        ]"
                    >
                    </v-select>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.start_date"
                        label="Start Date"
                        @blur="convertToDateObject($event, 'start_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.end_date"
                        label="End Date"
                        @blur="convertToDateObject($event, 'end_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.award_date"
                        label="Award Date"
                        @blur="convertToDateObject($event, 'award_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.final_acceptance_date"
                        label="Final Acceptance Date"
                        @blur="convertToDateObject($event, 'final_acceptance_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col cols="6">
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.notice_of_completion_date"
                        label="Notice of Completion Date"
                        @blur="convertToDateObject($event, 'notice_of_completion_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-textarea
                        label="Notes"
                        v-model.lazy="entityCore.notes"
                        variant="filled"
                        auto-grow
                        @blur="convertEmptyToNull('notes')"
                        :rules="[
                            rules.max(2000)
                        ]"
                    ></v-textarea>
                </v-col>
            </v-row>

            <v-row><v-col><v-divider></v-divider></v-col></v-row>

            <v-row>
                <v-col>
                    <h3>Adjustment Factors</h3>
                </v-col>
                <v-col class="d-flex ga-4 justify-end">
                    <v-btn color="primary" variant="outlined" @click="openAdjustmentFactorDialog()">
                        Add
                    </v-btn>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-data-table
                        :headers="adjustmentFactorsListHeaders"
                        :items="entityCore.adjustment_factors"
                        item-key="type"
                        :items-per-page="100"
                        :hide-default-footer="true"
                        disable-sort
                        >
                        <template v-slot:[`item.value`]="{ value }">
                            {{ value.toFixed(4) }}
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            
            <v-row><v-col><v-divider></v-divider></v-col></v-row>

            <v-row>
                <v-col>
                    <h3>Options</h3>
                </v-col>
                <v-col class="d-flex ga-4 justify-end">
                    <v-btn color="primary" variant="outlined" @click="openOptionDialog()">
                        Add
                    </v-btn>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-data-table
                        :headers="optionsListHeaders"
                        :items="entityCore.options"
                        item-key="type"
                        :items-per-page="100"
                        :hide-default-footer="true"
                        disable-sort
                        >
                    </v-data-table>
                </v-col>
            </v-row>

            <v-row v-show="!isLoading[`unit_price_book_${parent.type}-${parent.id}`]">
                <v-col>
                    <h3>Associated Unit Price Books</h3>
                    <v-checkbox
                        v-for="entity in unitPriceBooks"
                        :key="entity._id"
                        v-model="entityCore.associated_unit_price_books"
                        :label="entity._name"
                        :value="entity._id"
                        density="compact"
                        hide-details="true"
                    ></v-checkbox>
                </v-col>
            </v-row>

            <FormCreateFooter 
                :form="form"
                :entityCore="entityCore"
                :current="current"
                :parent="parent" 
            />
            
        </v-form>

        <AdjustmentFactorDialog 
            :show="showAdjustmentFactorDialog"
            :currentAdjustmentFactors="entityCore.adjustment_factors" 
            @adjustmentFactorAdded="addAdjustmentFactor"
            @closeDialog="closeAdjustmentFactorDialog"
        />
        
        <OptionDialog 
            :show="showOptionDialog"
            :currentOptions="entityCore.options" 
            @optionAdded="addOption"
            @closeDialog="closeOptionDialog"
        />

    </v-container>
</template>
<script setup>
import LoadingBar from "@/components/interface/LoadingBar.vue";
import FormCreateFooter from "@/components/interface/FormCreateFooter.vue";
import AdjustmentFactorDialog from "@/components/entity/contract/AdjustmentFactorDialog.vue";

import contractTypeList from "@/assets/selects/contract-type.json";
import contractProcurementTypeList from "@/assets/selects/contract-procurement-type.json";
import adjustmentFactorTypeList from "@/assets/selects/contract-adjustment-factor-type.json";
import { useStore } from 'vuex';
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import rules from "@/assets/modules/rules.js";
import { useFetchList, useFieldValidation, useFormatting } from "@/assets/modules/composables.js";


const route = useRoute();
const store = useStore();
const form = ref();
const isLoading = reactive({});
const alreadyFetchedData = [];
const { fetchList } = useFetchList(isLoading, alreadyFetchedData);
const { formatDate } = useFormatting();

// Define UPBs as an array for schema validation
const entityCore = ref({
    associated_unit_price_books: [],
    adjustment_factors: [],
    options: [],
});
const { numbersAndDecimalsOnly, convertToNumber, convertEmptyToNull, convertToDateObject } = useFieldValidation(entityCore);
const current = {
    type: "contract"
};
const parent = {
    id: route.params.parent_id,
    type: "owner"
};
const showAdjustmentFactorDialog = ref(false);


const ownerDoc = computed(() => {
    return store.getters.getEntityDocument(parent.id);
})
const unitPriceBooks = computed(() => {
    return store.getters.getChildList(`${parent.type}.${parent.id}`, "unit_price_book");
})

onMounted(() => {
    fetchList("unit_price_book",`${parent.type}-${parent.id}`);
})

const contractorsList = computed(() => {
    const loading = isLoading['contractor_all'];
    const disabled = !ownerDoc.value?._id;
    const items = store.getters.getContractorList(ownerDoc.value?._id);
    const hint = ownerDoc.value?._id ? `List of contractors for ${ownerDoc.value._name}`:'List of contractors';
    return {
        items: items,
        hint: hint,
        loading: loading,
        disabled: disabled,
    }
})

function addAdjustmentFactor(adjustmentFactor){
    entityCore.value.adjustment_factors.push(adjustmentFactor);
}

function getTitleFromValue(value) {
    const item = adjustmentFactorTypeList.find(item => item.value === value);
    return item ? item.title : value;
}
const adjustmentFactorsListHeaders = [
    {
        title: "Value",
        key: "value",
    },
    {
        title: "Type",
        key: "type",
        value: (row) => getTitleFromValue(row.type),
    },
];


function openAdjustmentFactorDialog(){
    showAdjustmentFactorDialog.value = true;
}
function closeAdjustmentFactorDialog(){
    showAdjustmentFactorDialog.value = false;
}



import OptionDialog from "@/components/entity/contract/OptionDialog.vue";
const showOptionDialog = ref(false);
function addOption(option){
    entityCore.value.options.push(option);
}
const optionsListHeaders = [
    {
        title: "Year",
        key: "year",
    },
    {
        title: "Amount",
        key: "amount",
    },
    {
        title: "Start Date",
        key: "start_date",
        value: (row) => formatDate(row.start_date),
    },
    {
        title: "End Data",
        key: "end_date",
        value: (row) => formatDate(row.end_date),
    },
];
function openOptionDialog(){
    showOptionDialog.value = true;
}
function closeOptionDialog(){
    showOptionDialog.value = false;
}
</script>